import { useCallback, useEffect, useRef, useState } from "react";
import { analyticsWarehouseService } from "../../utils/services/analyticsWarehouseService";

export const ANALYTICS_WAREHOUSE_PAGE_SIZE = 50;

/**
 * Analytics-visible warehouse options for a client (server-side search and
 * paging). Configured clients get only their configured warehouses — a
 * handful, so one page; unconfigured clients page through every warehouse.
 */
const useAnalyticsWarehouses = (
  clientId,
  { search = "", enabled = true, pageSize = ANALYTICS_WAREHOUSE_PAGE_SIZE } = {},
) => {
  const [state, setState] = useState({
    options: [],
    total: null,
    configured: false,
    defaultWarehouse: null,
    loading: false,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);
  const controllerRef = useRef(null);

  const load = useCallback(
    async (skip) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const res = await analyticsWarehouseService.fetch({
          clientId,
          search,
          limit: pageSize,
          skip,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const rows = (Array.isArray(res?.data) ? res.data : []).filter((row) => row?._id);
        setState((prev) => ({
          options: skip ? [...prev.options, ...rows] : rows,
          total: Number.isFinite(res?.total) ? res.total : rows.length,
          configured: Boolean(res?.configured),
          defaultWarehouse: res?.default_warehouse || null,
          loading: false,
          error: null,
        }));
      } catch (error) {
        if (controller.signal.aborted || error?.name === "CanceledError") return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "Couldn't load warehouses.",
        }));
      }
    },
    [clientId, search, pageSize],
  );

  useEffect(() => {
    if (!enabled || !clientId) {
      setState((prev) => ({ ...prev, options: [], total: null, loading: false }));
      return undefined;
    }
    load(0);
    return () => controllerRef.current?.abort();
  }, [enabled, clientId, load, attempt]);

  const hasMore = state.total != null && state.options.length < state.total;
  const loadMore = useCallback(() => {
    if (!state.loading && hasMore) load(state.options.length);
  }, [hasMore, load, state.loading, state.options.length]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { ...state, hasMore, loadMore, retry };
};

export default useAnalyticsWarehouses;
